/* Stripe widget presentation adapter. No currency calculation, network calls,
 * input writes, plan selection, or billing logic. Shopify renders price fragments.
 * The app and purchase-options remain the owners of the purchase form. */
if (!customElements.get('rw-stripe-widget')) {
  customElements.define('rw-stripe-widget', class extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('product-info');
      if (!this.scope) return;
      this.onChange = () => this.schedule();
      this.scope.addEventListener('change', this.onChange);
      this.observer = new MutationObserver(() => this.schedule());
      this.observe();
      this.schedule();
    }

    observe() {
      this.observer.observe(this.scope, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['value', 'checked', 'disabled', 'style', 'data-price-ready']
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.scope?.removeEventListener('change', this.onChange);
    }

    schedule() {
      if (this.queued) return;
      this.queued = true;
      queueMicrotask(() => {
        this.queued = false;
        if (!this.isConnected) return;
        // Do not observe our own presentation writes or create a mutation loop.
        this.observer.disconnect();
        try { this.sync(); } finally { this.observe(); }
      });
    }

    text(node, value) {
      if (node && value && node.textContent !== value) node.textContent = value;
    }

    sync() {
      const widget = this.querySelector('.subscription-selector');
      const data = this.querySelector('[data-rw-stripe-data]');
      const form = this.scope.querySelector(`form[id="${CSS.escape(this.dataset.formId)}"]`);
      if (!widget || widget.dataset.productId !== this.dataset.productId || !data || !form) return;
      const variantId = form.elements.namedItem('id')?.value;
      const fragments = Array.from(data.querySelectorAll('template[data-rw-variant]'))
        .filter((template) => template.dataset.rwVariant === variantId);
      if (!fragments.length) return; // Unknown app/variant: leave its original UI intact.
      widget.classList.add('rw-stripe-active');
      const fragmentFor = (plan) => fragments.find((template) => template.dataset.rwPlan === plan);
      const options = Array.from(widget.querySelectorAll('.purchase-option'));
      const relevant = options.filter((option) => {
        const radio = option.querySelector('input[value="subscription"][data-selling-plan-id]');
        return radio && fragmentFor(radio.dataset.sellingPlanId);
      });
      const planInputs = Array.from(form.elements).filter((input) => input.name === 'selling_plan' && !input.disabled);
      const selected = widget.querySelector('input[value="subscription"][data-selling-plan-id]:checked');
      // Hide the duplicate alternative only AFTER the app has selected and wired
      // a valid plan. Never hide a currently selected or unsynchronised one-time choice.
      const validSelection = !!selected && !!fragmentFor(selected.dataset.sellingPlanId) &&
        planInputs.length > 0 && planInputs.every((input) => input.value === selected.dataset.sellingPlanId);
      widget.classList.toggle('rw-stripe-single', relevant.length === 1 && validSelection);
      for (const option of options) {
        const radio = option.querySelector('.purchase-type-radio');
        if (!radio) continue;
        const plan = radio.value === 'subscription' ? radio.dataset.sellingPlanId : '';
        const fragment = fragmentFor(plan);
        option.classList.toggle('rw-stripe-hide-onetime', radio.value === 'onetime' && validSelection);
        const price = option.querySelector('.purchase-option-price');
        price?.classList.toggle('rw-stripe-price-unavailable', !fragment);
        if (fragment && price) {
          const html = fragment.innerHTML.trim();
          if (price.innerHTML !== html) price.innerHTML = html;
          // Our Shopify price is ready regardless of the app's asynchronous proxy.
          price.classList.add('rw-stripe-price-ready');
        }
        // Only suppress duplicate text when the same text exists below the box.
        const description = option.querySelector('.subscription-description');
        const detail = Array.from(widget.querySelectorAll('.subscription-detail'))
          .find((item) => item.dataset.planId === plan);
        const note = detail?.querySelector('.subscription-note');
        if (description) description.classList.toggle('rw-stripe-duplicate',
          !!note && description.textContent.trim() === note.textContent.trim());
        if (radio.value === 'onetime') this.text(option.querySelector('.purchase-option-label'), data.dataset.onetime);
      }
      this.text(widget.querySelector('.subscription-title'), data.dataset.title);
      widget.querySelectorAll('.subscription-tag').forEach((node) => this.text(node, data.dataset.subscription));
      widget.querySelectorAll('.subscription-policy-link a').forEach((node) => this.text(node, data.dataset.policy));
    }
  });
}
